import { strings } from './strings';

// The schedules data comes from a Portuguese-only API. When the user is viewing
// the site in English we do an ad-hoc, case-preserving replacement of a small set
// of well-known day-type / exception terms. This is intentionally simple: it is a
// best-effort cosmetic translation for a legacy data source, not a full i18n layer.

// Canonical Portuguese term (lowercased) -> canonical English term (lowercased).
// The casing of each match is re-applied at replacement time (see applyCasing).
const translationMap = {
    'dias úteis': 'weekdays',
    'dia útil': 'weekday',
    'férias escolares': 'school holidays',
    'segundas-feiras': 'mondays',
    'terças-feiras': 'tuesdays',
    'quartas-feiras': 'wednesdays',
    'quintas-feiras': 'thursdays',
    'sextas-feiras': 'fridays',
    'segunda-feira': 'monday',
    'terça-feira': 'tuesday',
    'quarta-feira': 'wednesday',
    'quinta-feira': 'thursday',
    'sexta-feira': 'friday',
    'domingos': 'sundays',
    'domingo': 'sunday',
    'sábados': 'saturdays',
    'sábado': 'saturday',
    'feriados': 'holidays',
    'feriado': 'holiday',
    'segundas': 'mondays',
    'terças': 'tuesdays',
    'quartas': 'wednesdays',
    'quintas': 'thursdays',
    'sextas': 'fridays',
    'segunda': 'monday',
    'terça': 'tuesday',
    'quarta': 'wednesday',
    'quinta': 'thursday',
    'sexta': 'friday',
    'escolar': 'school days',
    'apenas': 'only',
    'exceto': 'except',
    // Seasons
    'primavera': 'spring',
    'verão': 'summer',
    'outono': 'autumn',
    'inverno': 'winter',
    // Portuguese public holidays (longer phrases first via the length sort below,
    // so e.g. "Sexta-feira Santa" wins over "Sexta-feira").
    'dia de ano novo': 'new year\'s day',
    'ano novo': 'new year',
    'carnaval': 'carnival',
    'quarta-feira de cinzas': 'ash wednesday',
    'quinta-feira santa': 'maundy thursday',
    'sexta-feira santa': 'good friday',
    'domingo de páscoa': 'easter sunday',
    'segunda-feira de páscoa': 'easter monday',
    'páscoa': 'easter',
    'dia da liberdade': 'freedom day',
    'dia do trabalhador': 'labour day',
    'dia do trabalho': 'labour day',
    'corpo de deus': 'corpus christi',
    'dia de portugal': 'portugal day',
    'assunção de nossa senhora': 'assumption day',
    'assunção': 'assumption',
    'implantação da república': 'republic day',
    'dia de todos os santos': 'all saints\' day',
    'todos os santos': 'all saints',
    'restauração da independência': 'restoration of independence',
    'imaculada conceição': 'immaculate conception',
    'véspera de natal': 'christmas eve',
    'dia de natal': 'christmas day',
    'natal': 'christmas'
};

// Letters that count as "word characters" for boundary detection, including the
// accented characters used in Portuguese. Default \b / \w do not handle accents.
const letters = 'A-Za-zÀ-ÿ';
const boundary = `[^${letters}]`;

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longest terms first so e.g. "Segunda-feira" wins over "Segunda".
const sortedTerms = Object.keys(translationMap).sort((a, b) => b.length - a.length);
const alternation = sortedTerms.map(escapeRegExp).join('|');

// Capture a leading boundary (or start of string) so adjacent words don't bleed
// into each other, and require a trailing boundary (or end of string).
const matcher = new RegExp(`(^|${boundary})(${alternation})(?=${boundary}|$)`, 'gi');

const isUpperCase = (value) => value === value.toUpperCase() && value !== value.toLowerCase();

// Word starts for title-casing are the string start or a space/hyphen — NOT every
// non-letter, so possessive apostrophes ("All Saints' Day") aren't broken into "Saints'S".
const titleCase = (value) =>
    value.replace(new RegExp(`(^|[\\s-])([${letters}])`, 'g'), (m, pre, char) => pre + char.toUpperCase());

// Re-apply the casing style of the original Portuguese match to the English term.
const applyCasing = (translated, original) => {
    if (isUpperCase(original)) {
        return translated.toUpperCase();
    }

    const firstLetter = original.match(new RegExp(`[${letters}]`));
    if (firstLetter && firstLetter[0] === firstLetter[0].toUpperCase()) {
        return titleCase(translated);
    }

    return translated;
};

/**
 * Translates known Portuguese schedule terms to English when the active language
 * is English. Any other language (or unknown text) is returned untouched.
 */
export const translateScheduleText = (text) => {
    if (!text || 'en' !== strings.locale) {
        return text;
    }

    return text.replace(matcher, (match, pre, term) => {
        const translated = translationMap[term.toLowerCase()];
        return pre + applyCasing(translated, term);
    });
};
